<template>
  <PerfectScrollbar
    ref="tableContent"
    class="table-content"
    :style="{ height: players.length < 1 && activities.length < 1 ? 'auto' : '100%' }"
    :options="{ wheelSpeed: 0.5 }"
    @scroll="onScroll"
    @dragover="dragOver"
    @drop="drop"
  >
    <div :style="tbody">
      <!-- Filter popup -->
      <filter-component
        v-if="isFilterVisible"
        :is-visible="isFilterVisible"
        :x="popupX"
        :y="popupY"
        @close="handleClose"
      />
      <custom-canvas
        :visible-rows="visibleRows"
        :visible-columns="visibleColumns"
        :start-x-position="startXPosition"
        :start-y-position="startYPosition"
        :visible-table-width="canvasWidth"
        :visible-table-height="canvasHeight"
        :get-px-from-row-index="getPxFromRowIndex"
        :get-px-from-column-index="getPxFromColumnIndex"
        :options="options"
      ></custom-canvas>
      <designer
        :visible-rows="visibleRows"
        :visible-columns="visibleColumns"
        :start-x-position="startXPosition"
        :start-y-position="startYPosition"
        :full-table-height="fullTableHeight"
        :full-table-width="fullTableWidth"
        :get-px-from-row-index="getPxFromRowIndex"
        :get-px-from-column-index="getPxFromColumnIndex"
        :get-index-at-column-px="getIndexAtColumnPx"
        :get-index-at-row-px="getIndexAtRowPx"
        :options="options"
      />
      <flow-context-menu v-if="flowMenu" @hide-toolbox="hideFlowToolbox" />
      <comment
        v-if="visibleComment.id"
        class="comment"
        :get-px-from-row-index="getPxFromRowIndex"
        :get-px-from-column-index="getPxFromColumnIndex"
      />
    </div>
    <vertical-drop-indicator v-if="dropIndicator.id && dropIndicator.x" v-bind="dropIndicator" />
  </PerfectScrollbar>
</template>

<script>
import DesignerMixin from "./DesignerMixin";
import Designer from "../designer/Designer.vue";
import { ProcessTypes } from "../../utils/icons";
import { DefaultWidths, DefaultHeights } from "./corner/CornerConstants";
import CustomCanvas from "../designer/CustomCanvas.vue";
import VerticalDropIndicator from "../process-design/VerticalDropIndicator.vue";
import { ColumnLetterHeight, Positions, EditModes, RowNumberWidth } from "../../utils/constants";
import { ColumnTypes } from "./cell/CellConstants";
import Comment from "./cell/Comment.vue";
import FilterComponent from "./structure-table/FilterDialog.vue";
import { calculateNewPlayerPosition } from "../../store/helpers/SmartSearchHelper";
import { PerfectScrollbar } from "vue3-perfect-scrollbar";
import FlowContextMenu from "./body/sidebar/flow/FlowContextMenu.vue";
export default {
  components: {
    Designer,
    CustomCanvas,
    VerticalDropIndicator,
    Comment,
    FlowContextMenu,
    FilterComponent,
    PerfectScrollbar,
  },
  mixins: [DesignerMixin],
  data() {
    return {
      fullTableHeight: 0,
      fullTableWidth: 0,
      visibleTableHeight: 0,
      visibleTableWidth: 0,
      previousXScrollPosition: 0,
      previousYScrollPosition: 0,
      overScan: 5,
      isScrolling: false,
      animationId: null,
      canvasWidth: 1000,
      canvasHeight: 1000,
      myObserver: null,
    };
  },
  computed: {
    isInEditMode() {
      return this.editMode !== EditModes.Disable;
    },
    editMode() {
      return this.$store.state.pdesign.editMode.value;
    },
    visibleComment() {
      return this.$store.state.cells.visibleComment;
    },
    firstVisiblePlayerIndex() {
      return this.columns.findIndex((item) => item.type === ColumnTypes.Player && this.getColumnVisibility(item.id));
    },
    dropIndicator() {
      return this.$store.state.cells.dropIndicator;
    },
    gridlines() {
      return this.design.showGridlines;
    },
    options() {
      return this.current.type === ProcessTypes.Grid
        ? {
            lines: this.gridlines,
            margin: 0,
            borderRadius: 0,
          }
        : {
            lines: false,
            margin: 3,
            borderRadius: 4,
          };
    },
    defaultColumnWidth() {
      return this.current.type === ProcessTypes.Grid
        ? DefaultWidths.Grid
        : this.current.type === ProcessTypes.Normal
        ? DefaultWidths.Default
        : DefaultWidths.Default / 2;
    },
    defaultRowHeight() {
      return this.current.type === ProcessTypes.Grid
        ? DefaultHeights.Grid
        : this.current.type === ProcessTypes.Normal
        ? DefaultHeights.Default
        : DefaultHeights.Default / 2;
    },
    isGrid() {
      return this.current.type === ProcessTypes.Grid;
    },
    hScroll() {
      return this.$store.state.cells.hScroll;
    },
    vScroll() {
      return this.$store.state.cells.vScroll;
    },
    heightArray() {
      const arr = [];
      this.rows?.forEach((row, index) => {
        if (this.getRawHeight(row.id) !== this.defaultRowHeight) {
          const lastIndex = arr[arr.length - 1]?.index ?? -1;
          const lastPositionY = arr[arr.length - 1]?.endCoord ?? 0;
          const currentY = lastPositionY + (index - lastIndex - 1) * this.defaultRowHeight;
          arr.push({ index, startCoord: currentY, endCoord: +currentY + +this.getRawHeight(row.id) });
        }
      });
      return arr.sort((a, b) => a.startCoord - b.startCoord);
    },
    widthArray() {
      const arr = [];
      this.columns?.forEach((column, index) => {
        if (this.getRawWidth(column.id) !== this.defaultColumnWidth) {
          const lastIndex = arr[arr.length - 1]?.index ?? -1;
          const lastPositionX = arr[arr.length - 1]?.endCoord ?? 0;
          const currentX = lastPositionX + (index - lastIndex - 1) * this.defaultColumnWidth;
          arr.push({ index, startCoord: currentX, endCoord: +currentX + +this.getRawWidth(column.id) });
        }
      });
      return arr.sort((a, b) => a.startCoord - b.startCoord);
    },
    tbody() {
      return {
        position: "relative",
        display: "inline-block",
        height: this.fullTableHeight + ColumnLetterHeight + (this.current.type !== ProcessTypes.Grid ? 34 : 0) + "px",
        maxHeight:
          this.fullTableHeight + ColumnLetterHeight + (this.current.type !== ProcessTypes.Grid ? 34 : 0) + "px",
        width: this.fullTableWidth + RowNumberWidth + "px",
        // https://stackoverflow.com/questions/43909940/why-does-overflowhidden-prevent-positionsticky-from-working
        // Sherlocks answer
        overflow: "clip",
      };
    },
    startYPosition() {
      return this.getPxFromIndex(this.vScroll.index, this.heightArray, this.defaultRowHeight);
    },
    startXPosition() {
      return this.getPxFromIndex(this.hScroll.index, this.widthArray, this.defaultColumnWidth);
    },
    visibleRows() {
      // dont remove below line. altough it seems unnecessary the application breaks
      const endIndex = Math.min(this.vScroll.end, this.rows.length);
      return this.rows.slice(this.vScroll.index, endIndex);
    },
    visibleColumns() {
      return this.columns.slice(this.hScroll.index, this.hScroll.end);
    },
    flowMenu() {
      return this.$store.state.customFlow.flowContextMenu;
    },
    isFilterVisible() {
      return this.$store.state.table.filterDialogVisible;
    },
    popupX() {
      return this.$store.state.table.filterPopupX;
    },
    popupY() {
      return this.$store.state.table.filterPopupY;
    },
  },
  watch: {
    columns(oldValue, newValue) {
      this.setTableWidth();
    },
    rows() {
      this.setTableHeight();
    },
    widthArray(newValue, oldValue) {
      this.canvasWidth = Math.ceil(
        this.getPxFromIndex(this.hScroll.end, newValue, this.defaultColumnWidth) -
          this.getPxFromIndex(this.hScroll.index, newValue, this.defaultColumnWidth) +
          this.defaultColumnWidth
      );
      this.setTableWidth();
    },
    heightArray(newValue, oldValue) {
      this.canvasHeight = Math.ceil(
        this.getPxFromIndex(this.vScroll.end, newValue, this.defaultRowHeight) -
          this.getPxFromIndex(this.vScroll.index, newValue, this.defaultRowHeight) +
          this.defaultRowHeight
      );
      this.setTableHeight();
    },
  },
  mounted() {
    window.addEventListener("dragend", this.dragEnd);
    this.setTableWidth();
    this.setTableHeight();
    this.myObserver = new ResizeObserver((entries) => {
      this.visibleTableWidth = entries[0].contentRect.width;
      this.visibleTableHeight = entries[0].contentRect.height;
      // previous scroll positions need to change because scroll function doesn't change anything if scroll position is not changed
      this.previousXScrollPosition = -1;
      this.previousYScrollPosition = -1;
      this.$nextTick(() => {
        this.$refs.tableContent?.$el?.dispatchEvent(new Event("scroll"));
        this.canvasWidth = Math.ceil(
          this.getPxFromIndex(this.hScroll.end, this.widthArray, this.defaultColumnWidth) -
            this.getPxFromIndex(this.hScroll.index, this.widthArray, this.defaultColumnWidth) +
            this.defaultColumnWidth
        );
        this.canvasHeight = Math.ceil(
          this.getPxFromIndex(this.vScroll.end, this.heightArray, this.defaultRowHeight) -
            this.getPxFromIndex(this.vScroll.index, this.heightArray, this.defaultRowHeight) +
            this.defaultRowHeight
        );
      });
    });
    this.myObserver.observe(this.$refs.tableContent?.$el);
  },
  unmounted() {
    window.removeEventListener("dragend", this.dragEnd);
    this.myObserver?.disconnect();
  },
  methods: {
    handleClose() {
      this.$store.dispatch("table/setFilterDialogVisible", false);
    },
    getPositionOnCanvas(px) {
      const tableContent = document.querySelector(".table-content");
      const tableContentBound = tableContent?.getBoundingClientRect();
      return px - tableContentBound.left - RowNumberWidth + tableContent.scrollLeft;
    },
    dragOver(e) {
      if (e.dataTransfer.types.includes("player") && this.current.type !== ProcessTypes.Grid) {
        e.preventDefault();
        if (this.visiblePlayers.length > 0) return this.setIndicator(e);

        this.$store.dispatch("cells/setdropIndicator", {
          id: this.columns[this.columns.length - 1].id,
          position: Positions.After,
        });
      }
    },
    async drop(e) {
      if (!e.dataTransfer.getData("player")) return;
      const dropIndicator = { ...this.dropIndicator };

      const id = e.dataTransfer.getData("id");
      if (!dropIndicator.id) return;

      const column = this.columnMap[dropIndicator.id];
      const index = column.index;

      const isBefore = dropIndicator.position === Positions.Before;
      const newCellIndex = calculateNewPlayerPosition(this.$store.state.cells, index, isBefore);

      // Check if the player already exists
      const playerExists = this.$store.getters["cells/playerExists"](id);
      if (playerExists) {
        this.$store.dispatch("cells/resetDropIndicator");
        return this.$store.dispatch("addToast", {
          ...this.$tm("processDesigner.duplicatePlayer.failed"),
          severity: "info",
          life: 3000,
        });
      }

      // Edit the Cell
      if (dropIndicator.position === Positions.Center) {
        const columnId = dropIndicator.id.includes("-Minus")
          ? dropIndicator.id.replace("-Minus", "-Plus")
          : dropIndicator.id;

        this.$store.dispatch("cells/editPlayer", {
          cellId: `Player_${columnId}`,
          newPlayerId: id,
          type: "player",
        });
      } else {
        // Add new cell before or after this cell
        const action = this.current.type === ProcessTypes.Posting ? "addPostingPlayer" : "addPlayer";
        this.$store.dispatch(`cells/${action}`, { ids: [id], index: newCellIndex });
      }
      this.$store.dispatch("cells/resetDropIndicator");
    },
    setIndicator(e) {
      let x = this.getPositionOnCanvas(e.clientX);
      let index = this.getIndexAtColumnPx(x);
      if (index < 0) return;
      if (index < this.firstVisiblePlayerIndex) index = this.firstVisiblePlayerIndex;
      let position;
      index = Math.min(index, this.columns.length - 1);
      const columnId = this.columns[index].id;
      const elementLeft = this.getPxFromColumnIndex(index);

      const width = this.getRawWidth(columnId);

      if (x < elementLeft + width / 4) {
        position = Positions.Before;
        x = elementLeft;
      } else if (x > elementLeft + 3 * (width / 4)) {
        position = Positions.After;
        x = elementLeft + width;
      } else {
        position = Positions.Center;
        x = elementLeft + width / 2;
      }

      this.$store.dispatch("cells/setdropIndicator", {
        id: columnId,
        position,
        x: x + 41,
      });
    },
    dragEnd(e) {
      this.$store.dispatch("cells/resetDropIndicator");
    },
    setTableWidth() {
      if (this.widthArray.length) {
        const lastDyanmicWidthColumn = this.widthArray[this.widthArray.length - 1];
        this.fullTableWidth =
          lastDyanmicWidthColumn.endCoord +
          (this.columns.length - lastDyanmicWidthColumn.index - 1) * this.defaultColumnWidth;
      } else {
        this.fullTableWidth = this.columns.length * this.defaultColumnWidth;
      }

      const tableContent = this.$refs.tableContent?.$el.getBoundingClientRect();
      this.visibleTableWidth = tableContent.width;
      const tableContentNode = document.querySelector(".table-content");
      const scrollLeft = tableContentNode?.scrollLeft ?? 0;
      const gridHScroll =
        this.getIndexAtPx(scrollLeft + this.visibleTableWidth, this.widthArray, this.defaultColumnWidth) +
        this.overScan;
      const hScroll = {
        index: this.hScroll.index ?? 0,
        end:
          this.current.type === ProcessTypes.Grid
            ? gridHScroll > this.columns.length
              ? this.columns.length
              : gridHScroll
            : this.columns.length,
      };
      this.$store.dispatch("cells/setHScroll", hScroll);
    },
    setTableHeight() {
      if (this.heightArray.length) {
        const lastDyanmicHeightRow = this.heightArray[this.heightArray.length - 1];
        this.fullTableHeight =
          lastDyanmicHeightRow.endCoord + (this.rows.length - lastDyanmicHeightRow.index - 1) * this.defaultRowHeight;
      } else {
        this.fullTableHeight = this.rows.length * this.defaultRowHeight;
      }
      const tableContent = this.$refs.tableContent?.$el.getBoundingClientRect();
      this.visibleTableHeight = tableContent.height;
      const tableContentNode = document.querySelector(".table-content");
      const scrollTop = tableContentNode?.scrollTop ?? 0;
      const gridVScroll =
        this.getIndexAtPx(scrollTop + this.visibleTableHeight, this.heightArray, this.defaultRowHeight) + this.overScan;
      const vScroll = {
        index: this.vScroll.index ?? 0,
        end:
          this.current.type === ProcessTypes.Grid
            ? gridVScroll > this.rows.length
              ? this.rows.length
              : gridVScroll
            : this.rows.length,
      };
      this.$store.dispatch("cells/setVScroll", vScroll);
    },
    getIndexAtPx(px, array, size) {
      const len = array.length;
      if (len === 0 || array[0].startCoord > px) return Math.floor(px / size);

      let left = 0;
      let right = len - 1;

      while (left <= right) {
        const mid = Math.floor((left + right) / 2);
        const { startCoord, endCoord, index } = array[mid];

        if (px >= startCoord && px <= endCoord) {
          return index;
        } else if (startCoord > px) {
          right = mid - 1;
        } else {
          left = mid + 1;
        }
      }

      return array[left - 1].index + 1 + Math.floor((px - array[left - 1].endCoord) / size);
    },
    getIndexAtColumnPx(px) {
      return this.getIndexAtPx(px, this.widthArray, this.defaultColumnWidth);
    },
    getIndexAtRowPx(px) {
      return this.getIndexAtPx(px, this.heightArray, this.defaultRowHeight);
    },
    getPxFromIndex(index, array, size) {
      if (!array.length || array[0].index > index) {
        return index * size;
      }

      for (let i = 0; i < array.length; i++) {
        const currentHeight = array[i];
        const nextHeight = array[i + 1];

        if (currentHeight.index === index) {
          return currentHeight.startCoord;
        }

        if (!nextHeight || nextHeight.index > index) {
          const leftOverIndex = index - currentHeight.index - 1;
          const leftOverPx = leftOverIndex * size;
          return currentHeight.endCoord + leftOverPx;
        }
      }
    },
    getPxFromRowIndex(index) {
      return this.getPxFromIndex(index, this.heightArray, this.defaultRowHeight);
    },
    getPxFromColumnIndex(index) {
      return this.getPxFromIndex(index, this.widthArray, this.defaultColumnWidth);
    },
    onScroll(event) {
      if (this.isInEditMode) {
        this.$store.dispatch("cells/setActiveCellLabel", true);
      }
      if (this.current.type !== ProcessTypes.Grid) return;
      if (event.target.scrollLeft !== this.previousXScrollPosition) {
        let scrollLeft = event.target.scrollLeft;

        const index = this.getIndexAtPx(scrollLeft, this.widthArray, this.defaultColumnWidth) - this.overScan;
        const end =
          this.getIndexAtPx(scrollLeft + this.visibleTableWidth, this.widthArray, this.defaultColumnWidth) +
          this.overScan;

        // show and hide 5 columns at a time
        const hScroll = {
          index: Math.max(0, index - (index % this.overScan)),
          end: Math.min(end + this.overScan - (end % this.overScan), this.columns.length),
        };
        this.$store.dispatch("cells/setHScroll", hScroll);
      }
      if (event.target.scrollTop !== this.previousYScrollPosition) {
        let scrollTop = event.target.scrollTop;

        const index = this.getIndexAtPx(scrollTop, this.heightArray, this.defaultRowHeight) - this.overScan;
        const end =
          this.getIndexAtPx(scrollTop + this.visibleTableHeight, this.heightArray, this.defaultRowHeight) +
          this.overScan;
        const vScroll = {
          index: Math.max(0, index - (index % this.overScan)),
          end: Math.min(end + this.overScan - (end % this.overScan), this.rows.length),
        };
        this.$store.dispatch("cells/setVScroll", vScroll);
      }
      this.previousXScrollPosition = event.target.scrollLeft;
      this.previousYScrollPosition = event.target.scrollTop;
    },
    hideFlowToolbox() {
      this.$store.dispatch("customFlow/setFlowContextMenu", null);
    },
  },
};
</script>
<style lang="less" scoped>
@import "../../assets/styles/variables";

.comment {
  overflow: hidden;
}

.table-content {
  position: relative;
  overflow: auto;
  border-collapse: collapse;
  display: block;
  width: 100%;
}

::-webkit-scrollbar {
  width: 16px;
  height: 14px;
  background: transparent;
}

::-webkit-scrollbar-thumb {
  background: #5292f780;
  border: 4px solid transparent;
  border-radius: 9px;
  background-clip: content-box;
}

::-webkit-scrollbar-button {
  display: block;
  background-repeat: no-repeat;
  background-size: 15px;
  background-position: center;
  border-radius: 4px;
  height: 14px;
  width: 12px;
  background-color: #5292f780;
}

::-webkit-scrollbar-button:hover {
  cursor: pointer;
}

/* Up */
::-webkit-scrollbar-button:vertical:start:decrement {
  background-image: url("../../assets/flags/chevron-up.svg");
}

::-webkit-scrollbar-button:vertical:start:increment {
  display: none;
}

/* Down */
::-webkit-scrollbar-button:vertical:end:decrement {
  display: none;
}

::-webkit-scrollbar-button:vertical:end:increment {
  background-image: url("../../assets/flags/chevron-down-3.svg");
}

/* Right */
::-webkit-scrollbar-button:horizontal:start:decrement {
  background-image: url("../../assets/flags/chevron-left.svg");
}

::-webkit-scrollbar-button:horizontal:start:increment {
  display: none;
}

/* Left */
::-webkit-scrollbar-button:horizontal:end:decrement {
  display: none;
}

::-webkit-scrollbar-button:horizontal:end:increment {
  background-image: url("../../assets/flags/chevron-right-2.svg");
}
</style>
